/**
 * PageHeader — ترويسة موحّدة لكل شاشات نطاق.
 *
 * لماذا واحدة: كانت كل شاشة تبدأ بشكل مختلف (h1 عارٍ هنا، SectionTitle هناك)
 * فبدا المنتج مجموعة صفحات لا وثيقة واحدة. الترويسة الموحّدة تجعل الانتقال بين
 * الشاشات يبدو تقليب ورقة في نفس المخطط لا فتح تطبيق آخر.
 *
 * عناصرها من لغة المخطط المساحي المعتمدة:
 *   · شبكة خطوط شعرية خلف الترويسة
 *   · سطر تعريف بخط أحادي المسافة (`NITAQ / DISTRICT`) — ختم الورقة
 *   · عنوان هادئ بوزن متوسط، لا وزن ثقيل ولا حجم صارخ
 *   · خط شعري سفلي يفصل الترويسة عن المحتوى كما يفصل إطار المخطط
 *
 * لا منطق هنا — عرض فقط.
 */

import type { ReactNode } from 'react';
import { color } from '@/design/tokens';

export function PageHeader({
  stamp,
  title,
  subtitle,
  action,
  meta,
}: {
  /** ختم الورقة — لاتيني قصير بخط أحادي المسافة. */
  stamp: string;
  title: string;
  subtitle?: string;
  /** عنصر يمين الترويسة (أزرار، مبدّلات). */
  action?: ReactNode;
  /** سطر أرقام أو حالة أسفل العنوان. */
  meta?: ReactNode;
}): JSX.Element {
  return (
    <section
      className="nq-grid nq-bleed"
      data-testid="page-header"
      style={{
        paddingBlock: '34px 24px',
        borderBottom: `1px solid ${color.hairlineSoft}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <span className="nq-scheme">{stamp}</span>
          <h1
            style={{
              marginTop: 12,
              fontSize: 30,
              fontWeight: 500,
              lineHeight: 1.35,
              letterSpacing: '-.01em',
            }}
          >
            {title}
          </h1>
          {subtitle ? (
            <p
              style={{
                marginTop: 8,
                fontSize: 13,
                lineHeight: 1.9,
                color: color.textSubtle,
                maxWidth: 520,
              }}
            >
              {subtitle}
            </p>
          ) : null}
          {meta ? <div style={{ marginTop: 14 }}>{meta}</div> : null}
        </div>

        {action ? <div style={{ flex: '0 1 auto' }}>{action}</div> : null}
      </div>
    </section>
  );
}

/**
 * شريط قياس — أرقام متجاورة يفصلها خط شعري رأسي، كجدول مسّاح.
 * الرقم بطل والوحدة تابعة، والعنوان أصغر ما يمكن مع بقائه فوق AA.
 */
export function MeasureStrip({
  items,
}: {
  items: { label: string; value: string; unit?: string; tone?: 'default' | 'accent' | 'warn' }[];
}): JSX.Element {
  return (
    <div
      data-testid="measure-strip"
      style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'stretch' }}
    >
      {items.map((m, i) => (
        <div key={m.label} style={{ display: 'flex', gap: 24 }}>
          {i > 0 ? (
            <div
              aria-hidden="true"
              style={{ width: 1, background: color.hairlineSoft, alignSelf: 'stretch' }}
            />
          ) : null}
          <div style={{ minWidth: 0 }}>
            <div
              className="nq-num"
              dir="ltr"
              style={{
                fontSize: 22,
                fontWeight: 600,
                color:
                  m.tone === 'accent'
                    ? color.teal
                    : m.tone === 'warn'
                      ? color.amberText
                      : color.text,
              }}
            >
              {m.value}
              {m.unit ? (
                <span style={{ fontSize: 11.5, marginInlineStart: 4, color: color.textSubtle }}>
                  {m.unit}
                </span>
              ) : null}
            </div>
            <div style={{ fontSize: 11, color: color.textSubtle, marginTop: 5 }}>{m.label}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
